<template>
  <div class="full-contract-renewal">
    <v-row class="ma-0 my-4 align-center">
      <h1 class="text-h5 font-weight-bold">Renovación de Contrato Completo</h1>
    </v-row>

    <!-- ─── Dialog de confirmación ─── -->
    <v-dialog v-model="confirmDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h6">¿Estás seguro?</v-card-title>
        <v-card-text class="text-body-1">{{ confirmMessage }}</v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn text :disabled="submitting" @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="submitting" @click="onConfirmed">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ─── Snackbar ─── -->
    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>

    <!-- ─── Panel de resultado ─── -->
    <div v-if="result">
      <v-row class="ma-0 mb-4 align-center justify-space-between">
        <div class="text-h6 font-weight-medium">Resultado de la migración</div>
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            v-if="nonMigratedItems.length > 0 && can('contract-renewal:export-failures')"
            outlined
            color="error"
            small
            @click="downloadNonMigratedCSV"
          >
            <v-icon left small>mdi-download</v-icon>Descargar omitidos/fallidos (CSV)
          </v-btn>
          <v-btn text color="primary" @click="resetAll">
            <v-icon left>mdi-refresh</v-icon>Nueva renovación
          </v-btn>
        </div>
      </v-row>

      <v-row class="mb-4">
        <v-col v-for="metric in RESULT_METRICS" :key="metric.key" cols="6" sm="3">
          <v-card outlined class="pa-3 text-center">
            <div class="text-h5 font-weight-bold" :class="metric.color">{{ result[metric.key] }}</div>
            <div class="caption grey--text">{{ metric.label }}</div>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="result.items && result.items.length > 0" class="mb-4">
        <div class="full-contract-renewal__section-label mb-2">Detalle por usuario</div>
        <v-simple-table dense class="elevation-1">
          <template v-slot:default>
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, i) in result.items"
                :key="i"
                :class="{ 'blue-grey lighten-5': item.status !== 'success' }"
              >
                <td>{{ item.userId }}</td>
                <td>
                  <v-chip x-small :color="statusColor(item.status)" dark>
                    {{ statusLabel(item.status) }}
                  </v-chip>
                </td>
                <td>
                  <span v-if="item.status === 'success'">
                    {{ item.migratedMembershipsCount }} membresía(s) migrada(s)
                    <span v-if="item.warnings && item.warnings.length > 0" class="error--text caption">
                      — {{ item.warnings.join(', ') }}
                    </span>
                  </span>
                  <span v-else class="error--text">{{ item.reason }}</span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </div>

    <!-- ─── Formulario ─── -->
    <v-card v-else outlined class="pa-6">
      <v-row dense>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="originContract"
            :items="contratos"
            item-text="label"
            item-value="id"
            label="Contrato origen *"
            dense
            outlined
            clearable
            :loading="loadingContratos"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="newContract"
            :items="contratos"
            item-text="label"
            item-value="id"
            label="Contrato destino *"
            dense
            outlined
            clearable
            :loading="loadingContratos"
            @change="onDestinationContractChange"
          />
        </v-col>
      </v-row>

      <v-alert v-if="sameContractSelected" dense text type="warning" class="mb-2">
        El contrato destino no puede ser el mismo que el contrato origen.
      </v-alert>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="newPlanId"
            :items="destinationPlans"
            item-text="label"
            item-value="planId"
            label="Plan destino *"
            dense
            outlined
            clearable
            :disabled="!newContract"
            :loading="loadingPlans"
            :no-data-text="newContract ? 'Este contrato no tiene planes activos' : 'Elige primero un contrato destino'"
          />
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <!-- El correo de renovación lo define la preferencia "Activación de membresía" del
             contrato destino, no esta pantalla. -->
        <v-col cols="12" md="6">
          <v-checkbox
            v-model="notifyDeactivation"
            label="Notificar baja por email"
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6" class="d-flex align-center">
          <span class="text-caption grey--text text--darken-1">
            El email de renovación se envía según las preferencias de correo del contrato destino.
          </span>
        </v-col>
      </v-row>

      <div class="mt-4">
        <v-btn v-if="can('contract-renewal:execute')" color="primary" :disabled="!canSubmit" @click="openConfirm">
          <v-icon left>mdi-send</v-icon>Renovar contrato
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import ContratoService from '@/services/contrato.service';
import CompanyService from '@/services/companie.service';
import MembershipsService from '@/services/memberships.service';
import { utilsMixin } from '@/mixins/utilsMixin';
import { can } from '@/utils/permissions';

// El backend ya valida SAME_CONTRACT_MIGRATION/CONTRATO_NOT_FOUND/
// PLAN_NOT_AVAILABLE_FOR_CONTRACT (ver contracts/renew-contract-consumption.md);
// estos mensajes solo traducen el `code` a algo que el admin entienda, sin
// duplicar la regla de negocio en el frontend.
const BATCH_ERROR_MESSAGES = {
  SAME_CONTRACT_MIGRATION: 'El contrato destino no puede ser el mismo que el contrato origen.',
  CONTRATO_NOT_FOUND: 'El contrato origen o destino ya no existe.',
  PLAN_NOT_AVAILABLE_FOR_CONTRACT: 'El plan elegido no es un plan activo del contrato destino.',
};

const RESULT_METRICS = [
  { key: 'requested', label: 'Considerados', color: 'blue-grey--text text--darken-1' },
  { key: 'migratedCount', label: 'Migrados', color: 'primary--text' },
  { key: 'skippedCount', label: 'Omitidos', color: 'blue-grey--text text--darken-1' },
  { key: 'failedCount', label: 'Fallidos', color: 'error--text' },
];

const STATUS_META = {
  success: { label: 'Migrado', color: 'primary' },
  skipped: { label: 'Omitido', color: 'blue-grey' },
  failed: { label: 'Fallido', color: 'error' },
};

export default {
  name: 'FullContractRenewal',

  mixins: [utilsMixin],

  data() {
    return {
      RESULT_METRICS,

      contratos: [],
      loadingContratos: false,

      originContract: null,
      newContract: null,
      newPlanId: null,
      destinationPlans: [],
      loadingPlans: false,

      notifyDeactivation: false,

      confirmDialog: false,
      confirmMessage: '',
      submitting: false,
      result: null,

      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
    };
  },

  computed: {
    sameContractSelected() {
      return !!this.originContract && !!this.newContract && this.originContract === this.newContract;
    },

    canSubmit() {
      return (
        !!this.originContract &&
        !!this.newContract &&
        !!this.newPlanId &&
        !this.sameContractSelected &&
        !this.submitting
      );
    },

    nonMigratedItems() {
      if (!this.result || !this.result.items) return [];
      return this.result.items.filter(item => item.status !== 'success');
    },
  },

  created() {
    this.loadContratos();
  },

  methods: {
    can,
    statusLabel(status) {
      return (STATUS_META[status] || {}).label || status;
    },

    statusColor(status) {
      return (STATUS_META[status] || {}).color || 'grey';
    },

    async loadContratos() {
      this.loadingContratos = true;
      const [contRes, compRes] = await Promise.all([
        ContratoService.getAll(),
        CompanyService.getCompanies(1, 500, ''),
      ]);
      if (contRes.status === 'ok' && compRes.status === 'ok') {
        const companiesArr = compRes.data?.companies || compRes.data || [];
        this.contratos = (contRes.data || []).map(c => {
          const empresa = companiesArr.find(e => e.id === c.companyId);
          return { ...c, label: empresa ? `${c.codigo} — ${empresa.name}` : c.codigo };
        });
      } else {
        this.showErrorSnackbar('Ocurrió un error al cargar los contratos');
      }
      this.loadingContratos = false;
    },

    async onDestinationContractChange() {
      this.newPlanId = null;
      this.destinationPlans = [];
      if (!this.newContract) return;

      this.loadingPlans = true;
      const res = await ContratoService.getById(this.newContract);
      if (res.status === 'ok') {
        this.destinationPlans = (res.data.planes || []).map(p => ({
          ...p,
          label: p.plan && p.plan.name ? p.plan.name : p.planId,
        }));
      } else {
        this.showErrorSnackbar('No se pudieron cargar los planes del contrato destino');
      }
      this.loadingPlans = false;
    },

    openConfirm() {
      const origen = this.contratos.find(c => c.id === this.originContract);
      const destino = this.contratos.find(c => c.id === this.newContract);
      const notifs = [];
      if (destino && destino.sendMembershipActivatedEmail !== false) notifs.push('email de renovación');
      if (this.notifyDeactivation) notifs.push('email de baja');
      const notifsText = notifs.length > 0 ? ` Se enviará ${notifs.join(' y ')}.` : ' No se enviará ningún email.';
      this.confirmMessage =
        `¿Estás seguro que deseas migrar todos los usuarios del contrato "${origen ? origen.label : this.originContract}" ` +
        `hacia "${destino ? destino.label : this.newContract}"? Esta acción no se puede deshacer fácilmente.${notifsText}`;
      this.confirmDialog = true;
    },

    async onConfirmed() {
      await this.submit();
      this.confirmDialog = false;
    },

    async submit() {
      this.submitting = true;
      try {
        const res = await MembershipsService.renewContract({
          originContract: this.originContract,
          newContract: this.newContract,
          newPlanId: this.newPlanId,
          notifyDeactivation: this.notifyDeactivation,
        });
        if (res.status === 'ok') {
          this.result = res.data;
          const isError = this.result.failedCount > 0;
          const message = isError
            ? `${this.result.migratedCount} migrado(s), ${this.result.failedCount} fallido(s).`
            : `${this.result.migratedCount} usuario(s) migrado(s) correctamente.`;
          if (isError) this.showErrorSnackbar(message);
          else this.showSuccessSnackbar(message);
        }
      } catch (error) {
        const message = BATCH_ERROR_MESSAGES[error.code] || error.message || 'Ocurrió un error al renovar el contrato';
        this.showErrorSnackbar(message);
      }
      this.submitting = false;
    },

    downloadNonMigratedCSV() {
      const headers = ['userId', 'status', 'reason'];
      const rows = this.nonMigratedItems.map(item => [item.userId, item.status, item.reason || '']);
      const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `renovacion-contrato-omitidos-fallidos_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    resetAll() {
      this.originContract = null;
      this.newContract = null;
      this.newPlanId = null;
      this.destinationPlans = [];
      this.notifyDeactivation = false;
      this.confirmDialog = false;
      this.confirmMessage = '';
      this.submitting = false;
      this.result = null;
    },
  },
};
</script>

<style scoped>
.full-contract-renewal {
  width: 100%;
}
.full-contract-renewal__section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1b1d21;
}
</style>
